/**
 * One linked transfer group (Phase 3, §6.5). Shows the group's legs with the
 * matched magnitude and a **Trennen** action that unlinks the group (sending
 * auto-linked legs back to the inbox). Unlinking is disabled offline.
 */
import { ArrowRight, Unlink } from 'lucide-react';
import { Button } from '../../components/ui/Button.tsx';
import { money } from '../../lib/format.ts';
import type { TransferGroupOut } from '../../lib/types.ts';
import { TransferLeg } from './TransferLeg.tsx';

export interface LinkedTransferRowProps {
  group: TransferGroupOut;
  onUnlink: (groupId: string) => void;
  unlinking: boolean;
  online: boolean;
}

export function LinkedTransferRow({ group, onUnlink, unlinking, online }: LinkedTransferRowProps) {
  const groupId = group.transfer_group_id;
  return (
    <div className="space-y-3 py-4">
      <div className="flex items-center gap-2">
        <span className="text-sm font-semibold tabular-nums text-slate-900 dark:text-slate-100">
          {money(group.amount)}
        </span>
        <span className="inline-flex items-center rounded-full bg-sky-100 px-2.5 py-0.5 text-xs font-semibold text-sky-800 dark:bg-sky-950 dark:text-sky-300">
          Verknüpft
        </span>
      </div>

      <div className="flex flex-wrap items-stretch gap-2">
        {group.legs.map((leg, index) => (
          <div key={leg.id} className="flex flex-1 items-stretch gap-2">
            <TransferLeg leg={leg} />
            {index < group.legs.length - 1 ? (
              <div className="flex shrink-0 items-center text-slate-400" aria-hidden>
                <ArrowRight className="h-5 w-5" />
              </div>
            ) : null}
          </div>
        ))}
      </div>

      {group.warnings.length > 0 ? (
        <ul className="space-y-1 text-xs text-amber-600 dark:text-amber-400">
          {group.warnings.map((warning) => (
            <li key={warning}>{warning}</li>
          ))}
        </ul>
      ) : null}

      <div className="flex items-center justify-end">
        <Button
          variant="secondary"
          size="sm"
          onClick={() => groupId && onUnlink(groupId)}
          disabled={unlinking || !online || !groupId}
          title={online ? undefined : 'Offline nicht möglich'}
        >
          <Unlink className="h-4 w-4" />
          {unlinking ? 'Trenne …' : 'Trennen'}
        </Button>
      </div>
    </div>
  );
}
